import { ChangeDetectionStrategy, Component, computed, input, ViewChild } from '@angular/core';
import { ToastMessageInterface } from 'src/app/core/interfaces/toast-message.interface';
import { AppDetailsApiService } from 'src/app/core/services/app-details-service/app-details-api.service';
import { AppDetailsService } from 'src/app/core/services/app-details-service/app-details.service';
import { ToastMessageService } from 'src/app/core/services/toast-message.service';
import { WidgetUIService } from '../../../../pages/start-page/services/widget-ui.service';
import { AddRemoveState } from '../../../enums/add-remove-state.enum';
import { AppInterface } from '../../../interfaces/app.interface';
import { AppInstanceAPIService } from '../../../services/appinstance-service/appinstance.service';
import { AddAppToFolderDialogComponent } from '../add-app-to-folder-dialog/add-app-to-folder-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'add-remove-app-button',
  imports: [AddAppToFolderDialogComponent, TranslatePipe],
  templateUrl: './add-remove-app-button.component.html',
  styleUrls: ['./add-remove-app-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRemoveAppButtonComponent {
  app = input.required<AppInterface>();
  recommendSectionName = input<string>('');
  isFromDetailPage = input<boolean>(false);
  recommendedSectionIdentification = input<string>('');
  protected showAddAppDialog: boolean = false;
  protected readonly AddRemoveState = AddRemoveState;
  protected state = computed(() =>
    this.app().inDashboard ? AddRemoveState.REMOVE : AddRemoveState.ADD,
  );
  @ViewChild('addAppToFolderDialog')
  private readonly addAppToFolderDialog!: AddAppToFolderDialogComponent;

  constructor(
    private readonly appInstanceService: AppInstanceAPIService,
    private readonly toastMessageService: ToastMessageService,
    private readonly widgetUIService: WidgetUIService,
    private readonly appDetailsService: AppDetailsApiService,
    private readonly appDetailsStateService: AppDetailsService,
    private readonly translate: TranslateService,
  ) {}

  addAppToFolder(): void {
    this.addAppToFolderDialog.openAddAppToFolderDialog();
  }

  removeAppInstance(): void {
    const message: ToastMessageInterface = {
      primaryMessage: this.app().title,
      secondMessage: this.translate.instant('common.toast.removed-successfully'),
    };
    this.appInstanceService.deleteAppInstance(this.app()).subscribe(() => {
      // Inform detail page in order to update the recommended apps list immediately
      if (this.app().appId) {
        this.appDetailsStateService.notifyAppAddedOrRemoved(this.app().appId!, false);
      }
      this.refreshAppDetails();
      this.toastMessageService.openToastDialog(message);
      /*
       * DXFINSIDEPORTAL-2806
       * Retrieve the app's representation from `WidgetUIService` to ensure consistency across the app state.
       * If the app is found in the `WidgetUIService` state, update its properties to mirror the changes.
       * `forceAllAppsSignalRecomputation` in `WidgetUIService` to ensure all reactive properties that depend on the
       * apps list are updated, triggering the UI to refresh.
       */
      const appFromWidgetUIService = this.widgetUIService
        .apps()
        .find((_app) => _app.appId === this.app().appId);
      if (appFromWidgetUIService) {
        appFromWidgetUIService.inDashboard = false;
        appFromWidgetUIService.appInstanceId = undefined;
        appFromWidgetUIService.folderId = undefined;
      }
      this.widgetUIService.forceAllAppsSignalRecomputation();
    });
  }

  onHoverOrTouch(): void {
    this.showAddAppDialog = true;
  }

  private refreshAppDetails() {
    if (this.app().appId) {
      this.appDetailsService.getAppDetails(this.app().appId!).subscribe();
    }
  }
}
